import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Box
} from '@mui/material';
import { authService } from '../../services/authService';

const DEFAULT_ORG_ENTITY_ID = '11111111-1111-1111-1111-111111111111';

export default function CreateUserDialog({ open, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadRoles();
      setEmail('');
      setDisplayName('');
      setPassword('');
      setSelectedRole('');
      setError(null);
    }
  }, [open]);

  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const data = await authService.listRoles();
      setRoles(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleSubmit = async () => {
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await authService.createUser({
        email,
        displayName: displayName || undefined,
        password,
        roleId: selectedRole || undefined,
        orgEntityId: selectedRole ? DEFAULT_ORG_ENTITY_ID : undefined
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create User</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</Alert>}

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          margin="normal"
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          helperText="Minimum 6 characters"
          disabled={loading}
        />

        <Box sx={{ mt: 2 }}>
          {loadingRoles ? (
            <CircularProgress size={24} />
          ) : (
            <FormControl fullWidth>
              <InputLabel>Role (optional)</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Role (optional)"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>No role - assign later</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || loadingRoles}>
          {loading ? <CircularProgress size={20} /> : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
